// src/components/FinishSignIn.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth } from '../firebase';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { toast } from 'react-toastify';

function FinishSignIn() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [needsEmail, setNeedsEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(true);

  useEffect(() => {
    const completeSignIn = async (emailToUse) => {
      try {
        setIsProcessing(true);
        // Completar el inicio de sesión con el enlace
        await signInWithEmailLink(auth, emailToUse, window.location.href);
        // Limpiar el correo electrónico almacenado
        window.localStorage.removeItem('emailForSignIn');
        toast.success(t('login.signInLinkSent'));
        // Redirigir al usuario a su inbox
        navigate('/inbox');
      } catch (error) {
        console.error('Error completing sign-in with email link:', error);
        toast.error(t('login.errorSendingLink'));
        setIsProcessing(false);
      }
    };

    const checkSignInLink = async () => {
      // Verificar si la URL contiene un enlace de inicio de sesión
      if (!isSignInWithEmailLink(auth, window.location.href)) {
        toast.error(t('login.errorSendingLink'));
        setIsProcessing(false);
        return;
      }

      // Obtener el correo electrónico almacenado en localStorage
      const storedEmail = window.localStorage.getItem('emailForSignIn');
      
      if (storedEmail) {
        // Si tenemos el email guardado, completar el inicio de sesión automáticamente
        await completeSignIn(storedEmail);
      } else {
        // Si no hay email guardado, mostrar el formulario
        setNeedsEmail(true);
        setIsProcessing(false);
      }
    };

    checkSignInLink();
  }, [navigate, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error(t('login.pleaseEnterValidEmail'));
      return;
    }

    try {
      setIsProcessing(true);
      // Guardar el email por si lo necesitamos de nuevo
      window.localStorage.setItem('emailForSignIn', email);
      
      // Completar el inicio de sesión con el enlace
      await signInWithEmailLink(auth, email, window.location.href);
      
      // Limpiar el correo electrónico almacenado
      window.localStorage.removeItem('emailForSignIn');
      toast.success('Sign-in successful!');
      
      // Redirigir al usuario a su perfil
      navigate('/profile');
    } catch (error) {
      console.error('Error completing sign-in with email link:', error);
      toast.error('Failed to complete sign-in. Please try again.');
      setIsProcessing(false);
    }
  };

  if (needsEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
            {t('login.continue')}
          </h2>
          <p className="text-gray-600 mb-6 text-center">
            {t('login.toGetStarted')}
          </p>
          
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                {t('login.emailAddress')}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                required
                autoFocus
              />
            </div>
            
            <button
              type="submit"
              disabled={isProcessing}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isProcessing ? t('login.sendingSignInLink') : t('login.continue')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mb-4"></div>
        <p className="text-gray-700">{t('login.sendingSignInLink')}</p>
      </div>
    </div>
  );
}

export default FinishSignIn;
